import React from 'react';
import { ArrowUp } from 'lucide-react';
import { useRealTimeChat } from '../context/RealTimeChatContext';
import { safeRenderText } from '../../../utils/safeRenderText';
import seenIcon from '../../../assets/images/seen.png';

const formatRole = (role) => {
    if (!role) return '';
    return role
        .split(/[_-]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

const getAvatarColor = (name) => {
    if (!name) return '#64748B';
    const colors = ['#FF5526', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#6366F1', '#14B8A6', '#F97316'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
};

const getThreadId = (thread) => thread?.thread_id || thread?.id || thread?._id;

const getThreadTitle = (thread, loggedInUserId) => {
    if (!thread) return 'Untitled thread';

    // 1. Handle Personal 1-on-1 Chats
    if (thread.chat_category === "personal" || thread.is_group === false) {
        if (thread.participants && Array.isArray(thread.participants)) {
            // Find the person who is NOT the current logged-in user
            const otherPerson = thread.participants.find(p => String(p.user_id) !== String(loggedInUserId));
            if (otherPerson && otherPerson.display_name) {
                return otherPerson.display_name;
            }
            // Fallback: If you are chatting with yourself (Saved Notes)
            if (thread.participants.length === 1 && String(thread.participants[0].user_id) === String(loggedInUserId)) {
                return (thread.participants[0].display_name || "You") + " (You)";
            }
        }
    }

    // 2. Handle Group Chats
    return (
        thread.thread_name ||
        thread.display_name ||
        thread.title ||
        thread.name ||
        (thread.scope_type === 'RFP_GENERAL' ? 'Everyone on this proposal' : 'Conversation')
    );
};

const getMessageId = (message) => message?.message_id || message?.id || message?._id;

const getMessageText = (message) => {
    if (!message) return '';
    if (typeof message === 'string') return message;

    const resolveText = (val) => {
        if (!val) return '';
        if (typeof val === 'string') return val;
        if (typeof val === 'object') {
            return val.body || val.content || val.text || JSON.stringify(val);
        }
        return String(val);
    };

    let text = message.body || message.content || message.text || '';
    if (typeof text === 'object' && text !== null) {
        text = resolveText(text);
    }
    if (typeof text === 'object' && text !== null) {
        text = JSON.stringify(text);
    }
    return text;
};

const getMessageAuthor = (message) =>
    message?.author_display_name || message?.sender_display_name || message?.author_name || message?.author || 'Unknown';

const getLocalDateString = (timestamp) => {
    if (!timestamp) return '';
    let normalized = timestamp;
    if (typeof timestamp === "string" && timestamp.includes("T") && !timestamp.endsWith("Z") && !/\+\d{2}:?\d{2}$/.test(timestamp) && !/-\d{2}:?\d{2}$/.test(timestamp)) {
        normalized = `${timestamp}Z`;
    }
    const d = new Date(normalized);
    if (isNaN(d.getTime())) return '';

    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (d.toDateString() === today.toDateString()) {
        return 'Today';
    } else if (d.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
    }

    return d.toLocaleDateString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

const getLocalTimeString = (timestamp) => {
    if (!timestamp) return '';
    let normalized = timestamp;
    if (typeof timestamp === "string" && timestamp.includes("T") && !timestamp.endsWith("Z") && !/\+\d{2}:?\d{2}$/.test(timestamp) && !/-\d{2}:?\d{2}$/.test(timestamp)) {
        normalized = `${timestamp}Z`;
    }
    const d = new Date(normalized);
    if (isNaN(d.getTime())) return '';

    const formatted = d.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });

    return formatted.replace(/([ap]\.?\s?m\.?)/i, (match) => {
        return match.toUpperCase().replace(/\./g, '').replace(/\s/g, '');
    });
};

// Render message text with @mentions highlighted
const renderMentionText = (text, currentUserId, participants = []) => {
    if (!text || typeof text !== 'string') return text;

    // Generate list of matchable names (full display name, first name, and "everyone")
    const candidateNames = ['everyone', 'Everyone'];
    if (participants.length) {
        participants.forEach(p => {
            if (!p.display_name) return;
            const fullName = p.display_name.trim();
            if (fullName) {
                candidateNames.push(fullName);
                const firstName = fullName.split(/\s+/)[0];
                if (firstName && firstName.length > 1) {
                    candidateNames.push(firstName);
                }
            }
        });
    }

    const uniqueNames = [...new Set(candidateNames)].sort((a, b) => b.length - a.length);
    if (!uniqueNames.length) return text;

    const escapedNames = uniqueNames.map(name => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(`(@(?:${escapedNames.join('|')}))`, 'gi');
    const parts = text.split(pattern);

    return parts.map((part, i) => {
        if (!part.startsWith('@')) return part;
        const mentionedName = part.slice(1).trim().toLowerCase();

        if (mentionedName === 'everyone') {
            return (
                <span key={i} style={{
                    color: '#FF5526',
                    fontWeight: '600',
                    background: 'rgba(255, 85, 38,0.1)',
                    borderRadius: '4px',
                    padding: '1px 4px',
                }}>{part}</span>
            );
        }

        // Find matching participant by either full display name or first name (case-insensitive)
        const matched = participants.find(p => {
            if (!p.display_name) return false;
            const fullNameLower = p.display_name.trim().toLowerCase();
            const firstNameLower = p.display_name.trim().split(/\s+/)[0].toLowerCase();
            const queryLower = mentionedName;
            return fullNameLower === queryLower || firstNameLower === queryLower;
        });

        if (!matched) return part;
        const isSelf = String(matched.user_id) === String(currentUserId);
        const color = isSelf ? '#F97316' : '#FF5526';
        return (
            <span key={i} style={{
                color,
                fontWeight: '600',
                background: isSelf ? 'rgba(249,115,22,0.1)' : 'rgba(255, 85, 38,0.1)',
                borderRadius: '4px',
                padding: '1px 4px',
            }}>{part}</span>
        );
    });
};

// Extract mentioned_user_ids from message text using participants list
const extractMentionedUserIds = (text, participants = []) => {
    if (!text || typeof text !== 'string') return [];
    const ids = [];
    const textLower = text.toLowerCase();

    // Check for @everyone mention
    if (textLower.includes('@everyone')) {
        participants.forEach(p => {
            if (p.user_id) {
                ids.push(p.user_id);
            }
        });
    }

    participants.forEach(p => {
        if (!p.display_name) return;
        const fullName = p.display_name.trim().toLowerCase();
        const firstName = p.display_name.trim().split(/\s+/)[0].toLowerCase();

        if (textLower.includes(`@${fullName}`) || (firstName && firstName.length > 1 && textLower.includes(`@${firstName}`))) {
            ids.push(p.user_id);
        }
    });
    return [...new Set(ids)];
};

const isMessageSelf = (message, currentUserId) => {
    // 1. Extract sender ID from all possible fields
    const senderId =
        message?.sender_user_id ||
        message?.sender_id ||
        message?.user_id ||
        message?.author_id ||
        message?.sender?.id ||
        message?.sender?.userId;

    // 2. STRIKE SYSTEM: Force a strict ID comparison. 
    // If we have both IDs, we rely ONLY on them.
    if (senderId && currentUserId) {
        return String(senderId) === String(currentUserId);
    }

    // 3. Fallback only if IDs are missing (should not happen with latest backend)
    return !!message?.is_from_current_user;
};

const isWithin24Hours = (createdTime) => {
    if (!createdTime) return false;
    const hrs = (Date.now() - new Date(createdTime).getTime()) / (1000 * 60 * 60);
    return hrs < 24;
};

const TeamChat = ({
    hasRfpContext,
    rfpId,
    proposalTitle,
    threads,
    selectedThreadId,
    messages,
    loadingThreads,
    loadingMessages,
    sendingMessage,
    error,
    onSelectThread,
    onSendMessage,
    onOpenWizard,
    currentUserId,
    typingUsers = [],
    onEditMessage,
    onDeleteMessage,
    onRenameThread,
    connectionStatus,
    readWatermarks = {},
}) => {
    const [input, setInput] = React.useState('');
    const messagesEndRef = React.useRef(null);
    const inputRef = React.useRef(null);

    // States for thread renaming
    const [isRenaming, setIsRenaming] = React.useState(false);
    const [renameTitle, setRenameTitle] = React.useState('');

    // States for @ mentions
    const [mentionQuery, setMentionQuery] = React.useState('');
    const [showMentions, setShowMentions] = React.useState(false);
    const [mentionStartIdx, setMentionStartIdx] = React.useState(-1);

    // States for message editing
    const [editingMessageId, setEditingMessageId] = React.useState(null);
    const [editBody, setEditBody] = React.useState('');

    const { sendJsonMessage } = useRealTimeChat();
    const isTypingRef = React.useRef(false);
    const stopTypingTimeoutRef = React.useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    React.useEffect(() => {
        scrollToBottom();
    }, [messages, typingUsers]);

    const selectedThread = React.useMemo(
        () => threads.find((thread) => String(getThreadId(thread)) === String(selectedThreadId)),
        [selectedThreadId, threads]
    );

    const isDM = React.useMemo(() => {
        if (!selectedThread) return false;
        const isGrp = String(selectedThread.chat_category || '').toLowerCase() === 'group' ||
            selectedThread.is_group === true ||
            selectedThread.scope_type === 'RFP_GENERAL' ||
            selectedThread.scope_type === 'RFP_SECTION';
        return !isGrp;
    }, [selectedThread]);

    // Derive participants list from selected thread (after selectedThread is initialized)
    const mentionableUsers = React.useMemo(() => {
        const base = [
            {
                user_id: 'everyone',
                display_name: 'Everyone',
                role: 'all_participants'
            }
        ];
        if (!selectedThread?.participants) return base;
        const list = selectedThread.participants.filter(
            p => String(p.user_id) !== String(currentUserId) && p.display_name
        );
        return [...base, ...list];
    }, [selectedThread, currentUserId]);

    const filteredMentions = React.useMemo(() => {
        if (!mentionQuery) return mentionableUsers;
        return mentionableUsers.filter(p =>
            p.display_name.toLowerCase().includes(mentionQuery.toLowerCase())
        );
    }, [mentionableUsers, mentionQuery]);

    React.useEffect(() => {
        if (selectedThread) {
            setRenameTitle(getThreadTitle(selectedThread, currentUserId));
        }
        setIsRenaming(false);
    }, [selectedThreadId, selectedThread, currentUserId]);

    const handleRenameSubmit = async () => {
        if (!renameTitle.trim() || !onRenameThread || !selectedThreadId) return;
        const ok = await onRenameThread(selectedThreadId, renameTitle.trim());
        if (ok) {
            setIsRenaming(false);
        }
    };

    const handleStartEdit = (msg) => {
        setEditingMessageId(getMessageId(msg));
        setEditBody(getMessageText(msg));
    };

    const handleSaveEdit = async (msgId) => {
        if (!editBody.trim() || !onEditMessage) return;
        const ok = await onEditMessage(msgId, editBody.trim());
        if (ok) {
            setEditingMessageId(null);
        }
    };

    const handleSend = async () => {
        if (!input.trim() || sendingMessage) return;

        const currentInput = input;
        setInput('');
        setShowMentions(false);

        // Stop typing status instantly upon send
        if (isTypingRef.current && sendJsonMessage && selectedThreadId) {
            isTypingRef.current = false;
            if (stopTypingTimeoutRef.current) clearTimeout(stopTypingTimeoutRef.current);
            sendJsonMessage({
                event: 'typing',
                thread_id: selectedThreadId,
                is_typing: false
            });
        }

        // Extract mentioned user IDs from input text
        const allParticipants = selectedThread?.participants || [];
        const mentionedIds = extractMentionedUserIds(currentInput, allParticipants);

        const ok = await onSendMessage(currentInput.trim(), mentionedIds);
        if (!ok) {
            setInput(currentInput);
        }
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setInput(val);

        // --- @ mention detection ---
        const cursorPos = e.target.selectionStart;
        const textUpToCursor = val.slice(0, cursorPos);
        const atIndex = textUpToCursor.lastIndexOf('@');
        if (atIndex !== -1 && (atIndex === 0 || /\s/.test(val[atIndex - 1]))) {
            const query = textUpToCursor.slice(atIndex + 1);
            if (!query.includes(' ')) {
                setMentionStartIdx(atIndex);
                setMentionQuery(query);
                setShowMentions(true);
            } else {
                setShowMentions(false);
            }
        } else {
            setShowMentions(false);
        }

        if (!selectedThreadId || !sendJsonMessage) return;

        // If starting to type
        if (!isTypingRef.current && val.trim().length > 0) {
            isTypingRef.current = true;
            sendJsonMessage({
                event: 'typing',
                thread_id: selectedThreadId,
                is_typing: true
            });
        }

        // Reset inactivity timer
        if (stopTypingTimeoutRef.current) {
            clearTimeout(stopTypingTimeoutRef.current);
        }

        // Send is_typing: false if input cleared or idle
        if (val.trim().length === 0) {
            isTypingRef.current = false;
            sendJsonMessage({
                event: 'typing',
                thread_id: selectedThreadId,
                is_typing: false
            });
        } else {
            stopTypingTimeoutRef.current = setTimeout(() => {
                isTypingRef.current = false;
                sendJsonMessage({
                    event: 'typing',
                    thread_id: selectedThreadId,
                    is_typing: false
                });
            }, 3000);
        }
    };

    const handleMentionSelect = (user) => {
        const before = input.slice(0, mentionStartIdx);
        const after = input.slice(mentionStartIdx + 1 + mentionQuery.length);
        const newVal = `${before}@${user.display_name} ${after}`;
        setInput(newVal);
        setShowMentions(false);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    return (
        <main className="v-ch-main">


            <div className="v-ch-messages">
                {error ? <div className="v-ch-error-banner">{error}</div> : null}

                {/* Loading spinner — shown instead of messages */}
                {hasRfpContext && selectedThreadId && loadingMessages && (
                    <div className="v-ch-loader-center">
                        <div className="v-ch-spinner" />
                        <span className="v-ch-loader-text">Loading messages…</span>
                    </div>
                )}

                {/* Loading conversations spinner */}
                {hasRfpContext && !selectedThreadId && loadingThreads && (
                    <div className="v-ch-loader-center">
                        <div className="v-ch-spinner" />
                        <span className="v-ch-loader-text">Loading conversations…</span>
                    </div>
                )}

                {/* Non-RFP context */}
                {!hasRfpContext && (
                    <div className="v-ch-loader-center">
                        <span className="v-ch-loader-text">Team chat is available within an RFP.</span>
                    </div>
                )}

                {/* No thread selected */}
                {hasRfpContext && !selectedThreadId && !loadingThreads && (
                    <div className="v-ch-loader-center">
                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" style={{ marginBottom: '12px', opacity: 0.35 }}>
                            <circle cx="24" cy="24" r="20" stroke="#94A3B8" strokeWidth="2" />
                            <path d="M16 24h16M24 16l8 8-8 8" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="v-ch-loader-text">Select a thread to view messages.</span>
                    </div>
                )}

                {/* Empty thread */}
                {hasRfpContext && selectedThreadId && !loadingMessages && messages.length === 0 && (
                    <div className="v-ch-loader-center">
                        <div className="v-ch-custom-empty-state-illustration">
                            <svg width="96" height="96" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="60" cy="60" r="44" fill="#FFF1EC" />
                                <g filter="drop-shadow(0px 3px 6px rgba(255, 85, 38,0.15))">
                                    <rect x="42" y="44" width="38" height="26" rx="12" fill="#FF5526" />
                                    <path d="M48 70L44 76L52 70H48Z" fill="#FF5526" />
                                    <circle cx="53" cy="57" r="2.2" fill="white" />
                                    <circle cx="61" cy="57" r="2.2" fill="white" />
                                    <circle cx="69" cy="57" r="2.2" fill="white" />
                                </g>
                            </svg>
                        </div>
                        <h3 className="v-ch-custom-empty-state-title">Start a conversation</h3>
                        <p className="v-ch-custom-empty-state-description">
                            Ask questions, get updates, or collaborate with your team.
                        </p>
                    </div>
                )}
                {(() => {
                    let lastDateStr = null;
                    return messages.map((msg) => {
                        const self = isMessageSelf(msg, currentUserId);
                        const msgId = getMessageId(msg);
                        const author = getMessageAuthor(msg);
                        const text = getMessageText(msg);
                        const role = msg?.sender_role || msg?.role || '';
                        const messageKind = Number(msg?.message_kind || 0);
                        const canModify = self && isWithin24Hours(msg.created_at);

                        const dateStr = getLocalDateString(msg.created_at);
                        const showDateHeader = dateStr && dateStr !== lastDateStr;
                        if (showDateHeader) {
                            lastDateStr = dateStr;
                        }

                        const threadWatermark = readWatermarks[selectedThreadId];
                        const msgTime = new Date(msg.created_at).getTime();
                        const isReadByOther =
                            (msg.read_by && msg.read_by.some(uid => String(uid) !== String(currentUserId))) ||
                            msg.is_read ||
                            (threadWatermark && msgTime <= threadWatermark);

                        const readReceiptIcon = isReadByOther ? (
                            <img src={seenIcon} alt="Seen" style={{ width: '12px', height: '10px', flexShrink: 0, objectFit: 'contain', color: 'blue' }} />
                        ) : (
                            // Tick inside circle for sent
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                <circle cx="12" cy="12" r="10" />
                                <path d="m9 12 2 2 4-4" />
                            </svg>
                        );

                        return (
                            <React.Fragment key={msgId}>
                                {showDateHeader && (
                                    <div className={`v-ch-date-separator ${dateStr && dateStr.toLowerCase() === 'today' ? 'v-ch-date-separator--no-line' : ''}`}>
                                        <span className="v-ch-date-separator-text">{dateStr}</span>
                                    </div>
                                )}
                                <div
                                    className={`v-ch-msg ${self ? 'v-ch-msg--self' : ''} ${messageKind === 1 ? 'v-ch-msg--system' : ''}`}
                                    style={
                                        messageKind === 1
                                            ? { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: '100%', gap: 0 }
                                            : self
                                                ? { flexDirection: 'row', alignSelf: 'flex-end', justifyContent: 'flex-end', gap: '8px', maxWidth: '85%' }
                                                : { gap: '8px', maxWidth: '85%' }
                                    }
                                >
                                    {messageKind === 1 ? (
                                        <div className="v-ch-system-note">{safeRenderText(text)}</div>
                                    ) : (
                                        <>
                                            {/* Avatar for other users */}
                                            {!self && !isDM && (
                                                <div
                                                    className="v-ch-avatar"
                                                    style={{
                                                        background: getAvatarColor(author),
                                                        marginBottom: '2px',
                                                        flexShrink: 0,
                                                        fontSize: '10px'
                                                    }}
                                                    title={author}
                                                >
                                                    {getInitials(author)}
                                                </div>
                                            )}

                                            {/* Column wrapping timing and bubble */}
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: self ? 'flex-end' : 'flex-start', maxWidth: '85%', minWidth: 0 }}>
                                                {/* Timing / Author Header */}
                                                <div style={{
                                                    marginBottom: '4px',
                                                    fontSize: '11px',
                                                    color: '#94A3B8',
                                                    display: 'flex',
                                                    gap: '8px',
                                                    alignItems: 'center',
                                                    justifyContent: self ? 'flex-end' : 'flex-start',
                                                    width: '100%'
                                                }}>
                                                    {!self && !isDM && <span style={{ fontWeight: '600', color: '#64748B' }}>{author}</span>}
                                                    <span>{getLocalTimeString(msg.created_at)}</span>
                                                </div>

                                                {/* Bubble Container */}
                                                <div className="v-ch-bubble-container" style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', justifyContent: self ? 'flex-end' : 'flex-start' }}>
                                                    {editingMessageId === msgId ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '220px' }}>
                                                            <input
                                                                type="text"
                                                                value={editBody}
                                                                onChange={(e) => setEditBody(e.target.value)}
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    border: '1px solid #CBD5E1',
                                                                    borderRadius: '8px',
                                                                    fontSize: '13px',
                                                                    outline: 'none',
                                                                    width: '100%'
                                                                }}
                                                                autoFocus
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleSaveEdit(msgId);
                                                                    if (e.key === 'Escape') setEditingMessageId(null);
                                                                }}
                                                            />
                                                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                                                <button onClick={() => handleSaveEdit(msgId)} style={{ padding: '2px 8px', background: '#FF5526', color: 'white', border: 'none', borderRadius: '4px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>Save</button>
                                                                <button onClick={() => setEditingMessageId(null)} style={{ padding: '2px 8px', background: '#E2E8F0', color: '#475569', border: 'none', borderRadius: '4px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="v-ch-bubble">
                                                            {typeof text === 'string' && text.includes('@')
                                                                ? renderMentionText(text, currentUserId, selectedThread?.participants || [])
                                                                : safeRenderText(text)}
                                                        </div>
                                                    )}
                                                    {canModify && editingMessageId !== msgId && (
                                                        <div className="v-ch-floating-toolbar">
                                                            <button onClick={() => handleStartEdit(msg)} className="v-ch-toolbar-btn" title="Edit Message">
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                                                                </svg>
                                                            </button>
                                                            <button onClick={() => { if (window.confirm('Are you sure you want to delete this message?')) { onDeleteMessage(msgId); } }} className="v-ch-toolbar-btn v-ch-toolbar-btn--danger" title="Delete Message">
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Status Icon for self */}
                                            {self && (
                                                <div style={{ display: 'flex', alignItems: 'center', alignSelf: 'flex-end', marginBottom: '4px', flexShrink: 0 }} title={isReadByOther ? "Seen" : "Sent"}>
                                                    {readReceiptIcon}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </React.Fragment>
                        );
                    });
                })()}

                {/* Real-time Typing Indicators */}
                {typingUsers && typingUsers.length > 0 && (
                    <div className="v-ch-typing-indicator-wrap" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        color: '#64748B',
                        fontSize: '11px',
                        fontWeight: '500',
                        fontStyle: 'italic'
                    }}>
                        <div className="typing-dots" style={{ display: 'flex', gap: '3px' }}>
                            <span className="dot" style={{ width: '4px', height: '4px', background: '#94A3B8', borderRadius: '50%', display: 'inline-block' }} />
                            <span className="dot" style={{ width: '4px', height: '4px', background: '#94A3B8', borderRadius: '50%', display: 'inline-block' }} />
                            <span className="dot" style={{ width: '4px', height: '4px', background: '#94A3B8', borderRadius: '50%', display: 'inline-block' }} />
                        </div>
                        <span>
                            {typingUsers.map(uid => {
                                const msg = messages.find(m => String(m.sender_user_id || m.sender_id || m.user_id) === String(uid));
                                return msg?.author_display_name || msg?.sender_display_name || msg?.author_name || 'Someone';
                            }).filter((v, i, a) => a.indexOf(v) === i).join(', ')} is typing...
                        </span>
                    </div>
                )}

                <div ref={messagesEndRef} style={{ float: 'left', clear: 'both' }} />
            </div>

            <div className="v-ch-input-bar" style={{ position: 'relative' }}>
                {/* @ Mention Dropdown */}
                {showMentions && filteredMentions.length > 0 && (
                    <div className="v-ch-mention-list">
                        <div className="v-ch-mention-header">Mention someone</div>
                        {filteredMentions.map((user) => (
                            <button
                                key={user.user_id}
                                className="v-ch-mention-item"
                                onMouseDown={(e) => { e.preventDefault(); handleMentionSelect(user); }}
                            >
                                <div className="v-ch-mention-avatar">
                                    {user.display_name.slice(0, 1).toUpperCase()}
                                </div>
                                <div className="v-ch-mention-info">
                                    <span className="v-ch-mention-name">{user.display_name}</span>
                                    {user.role && <span className="v-ch-mention-role">{formatRole(user.role)}</span>}
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                <div className="v-ch-input-composer">
                    <div className="v-ch-input-wrap">
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Type a message… (@ to mention)"
                            value={input}
                            onChange={handleInputChange}
                            disabled={!hasRfpContext || !selectedThreadId || sendingMessage}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') setShowMentions(false);
                                if (e.key === 'Enter' && !showMentions) handleSend();
                            }}
                            onBlur={() => setTimeout(() => setShowMentions(false), 150)}
                        />
                    </div>
                    <div className="v-ch-input-actions-right">
                        <button
                            className="v-ch-send-btn premium-send"
                            aria-label="Send message"
                            onClick={handleSend}
                            disabled={!hasRfpContext || !selectedThreadId || !input.trim() || sendingMessage}
                        >
                            <ArrowUp size={16} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default TeamChat;
